import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { forkJoin, map } from 'rxjs';
import { ButtonComponent } from '../../../../../../ui-components/lib/button/button.component';
import { MultiSelectComponent } from '../../../../../../ui-components/lib/multi-select/multi-select.component';
import {
  OptionItem,
  ToggleOption,
} from '../../../../../../ui-components/lib/option.interface';
import { SelectComponent } from '../../../../../../ui-components/lib/select/select.component';
import { ToggleComponent } from '../../../../../../ui-components/lib/toggle/toggle.component';
import { ValidationMessageComponent } from '../../../../../../ui-components/lib/validation-message/validation-message.component';
import {
  CustomWorkflowApiError,
  localized,
  rawValueSignal,
} from '../../custom-workflow/custom-workflow.model';
import { ReflectionScopeEnum } from '../enums/reflection-scope.enum';
import {
  extractConfigFields,
  extractConditionUserIds,
  extractConfigUserIds,
} from '../reflection-config-fields';
import { buildConfigGroup, toConfigPayload } from '../reflection-form.factory';
import {
  buildConditionsGroup,
  ReflectionConditionsComponent,
} from '../reflection-conditions/reflection-conditions.component';
import { ReflectionParamInputComponent } from '../reflection-param-input/reflection-param-input.component';
import {
  ConditionSet,
  Reflection,
  ReflectionField,
  ReflectionParam,
  ReflectionPayload,
} from '../reflections.models';
import { FormService } from '../../../../../../form-builder/services/form/form.service';
import { ReflectionsService } from '../reflections.service';

/** One config param with what the template needs to render it. */
interface ParamRowView {
  param: ReflectionParam;
  /** The param is a secret that already has a stored value. */
  hasSecret: boolean;
}

/** Server error key → the config control it belongs to (secrets live in the config group too). */
function errorPath(key: string): string[] {
  const path = key.split('.');
  if (path[0] === 'secrets') path[0] = 'config';
  return path;
}

/**
 * Create / edit form of one reflection: When (event), Do (action), Configure (generated from the action's
 * `config_schema`), Only if (conditions) and Options. It is not wrapped in a `<form>`, so the `lib-button`s
 * (which default to `type="submit"`) can never trigger a save by themselves.
 */
@Component({
  selector: 'lib-reflection-editor',
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    ButtonComponent,
    MultiSelectComponent,
    ReflectionConditionsComponent,
    ReflectionParamInputComponent,
    SelectComponent,
    ToggleComponent,
    ValidationMessageComponent,
  ],
  templateUrl: './reflection-editor.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReflectionEditorComponent implements OnInit {
  readonly service = inject(ReflectionsService);
  private readonly formService = inject(FormService);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  flowTemplateId = input.required<number>();
  scope = input.required<ReflectionScopeEnum>();
  stepTemplateId = input<number | null>(null);
  /** The reflection being edited; `null` creates a new one. */
  reflection = input<Reflection | null>(null);
  /** Fields of the flow template, for the params, conditions and watch fields. */
  fields = input.required<ReflectionField[]>();
  /** Emits the id of the created / updated reflection (the backend returns no reflection). */
  saved = output<number>();
  cancelled = output<void>();

  readonly form = new FormGroup({
    event: new FormControl<string | null>(null, Validators.required),
    hook_key: new FormControl<string | null>(null, Validators.required),
    config: new FormGroup<Record<string, AbstractControl>>({}),
    conditions: new FormGroup<Record<string, AbstractControl>>({}),
    skip_when_returned: new FormControl(false, { nonNullable: true }),
    watch_fields: new FormControl<string[]>([], { nonNullable: true }),
  });
  /** `config` and `conditions` are replaced as a whole, so the template binds them through signals. */
  readonly configGroup = signal<FormGroup>(this.form.controls.config);
  readonly conditionsGroup = signal<FormGroup>(this.form.controls.conditions);

  /** The form is shown once the catalog and the lookups are there and the names of the stored users are resolved. */
  readonly ready = signal(false);
  readonly isSaving = signal(false);
  /** Names of the users the stored config refers to, by id. */
  readonly userLabels = signal<Record<number, string>>({});
  /** Last 422 `errors` object (`config.title` → messages), mapped onto the controls. */
  readonly serverErrors = signal<Record<string, string[]>>({});
  /** Message of a failed save that carries no field errors. */
  private readonly saveMessage = signal<string | null>(null);

  private readonly hookKey = signal<string | null>(null);
  private readonly formValue = rawValueSignal(this.form);
  private readonly lang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });

  readonly isEdit = computed(() => this.reflection() !== null);
  readonly isFormScope = computed(
    () => this.scope() === ReflectionScopeEnum.FORM
  );

  /** Events of the scope, from the lookups (labelled by the backend in the active language). */
  readonly events = computed(
    () => this.service.lookups()?.events[this.scope()] ?? []
  );
  /** Catalog hooks that support the scope. */
  readonly actions = computed(
    () =>
      this.service
        .catalog()
        ?.hooks.filter(hook => hook.scopes.includes(this.scope())) ?? []
  );
  /** Config params of the selected action. */
  readonly schema = computed(
    () =>
      this.actions().find(action => action.key === this.hookKey())
        ?.config_schema ?? []
  );
  readonly operators = computed(() => this.service.lookups()?.operators ?? {});

  /** Field keys the current config references: what a form-scope reflection watches unless overridden. */
  readonly derivedWatch = computed(() => {
    this.formValue();
    const schema = this.schema();
    return extractConfigFields(
      schema,
      toConfigPayload(schema, this.configGroup()).config
    );
  });

  readonly storedSecrets = computed(() =>
    Object.entries(this.reflection()?.has_secrets ?? {})
      .filter(([, stored]) => stored)
      .map(([key]) => key)
  );

  readonly eventOptions = computed<OptionItem[]>(() =>
    this.events().map(event => ({ value: event.value, label: event.label }))
  );
  readonly actionOptions = computed<OptionItem[]>(() =>
    this.actions().map(action => ({
      value: action.key,
      label: localized(action.label, this.lang()),
    }))
  );
  readonly actionDescription = computed(() =>
    localized(
      this.actions().find(action => action.key === this.hookKey())?.description,
      this.lang()
    )
  );
  readonly paramRows = computed<ParamRowView[]>(() => {
    // A stored secret belongs to the action the reflection was saved with.
    const stored =
      this.hookKey() === this.reflection()?.hook_key
        ? this.storedSecrets()
        : [];
    return this.schema().map(param => ({
      param,
      hasSecret: stored.includes(param.key),
    }));
  });
  readonly placeholderOptions = computed(() =>
    (this.service.lookups()?.placeholders ?? []).map(placeholder => ({
      value: placeholder.value,
      label: placeholder.label,
    }))
  );
  readonly fieldOptions = computed<OptionItem[]>(() =>
    this.fields().map(field => ({
      value: field.value,
      label: localized(field.label, this.lang()),
    }))
  );
  readonly derivedWatchLabels = computed(() => {
    const labels = new Map(
      this.fieldOptions().map(option => [option.value, option.label])
    );
    return this.derivedWatch().map(key => labels.get(key) ?? key);
  });
  readonly skipToggleOption = computed<ToggleOption>(() => {
    this.lang();
    return {
      label: this.transloco.translate('reflection_skip_when_returned_label'),
      value: true,
    };
  });
  /** Every server message once, to show in one place (a field may have no input to show it next to). */
  readonly serverErrorMessages = computed(() => {
    const messages = [...new Set(Object.values(this.serverErrors()).flat())];
    const fallback = this.saveMessage();
    return messages.length || !fallback ? messages : [fallback];
  });

  constructor() {
    this.form.controls.hook_key.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(key => {
        if (key === this.hookKey()) return;
        // Errors about the previous action's config no longer apply.
        this.serverErrors.set({});
        this.saveMessage.set(null);
        this.rebuildConfig(key);
      });
  }

  ngOnInit(): void {
    if (this.service.catalog() && this.service.lookups()) {
      this.initialize();
    } else {
      forkJoin([this.service.loadCatalog(), this.service.loadLookups()])
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.initialize());
    }
  }

  /** Seeds the form from the reflection (edit) or the scope's only event (create). */
  private initialize(): void {
    const reflection = this.reflection();
    const events = this.events();
    this.form.patchValue(
      {
        event:
          reflection?.event ?? (events.length === 1 ? events[0].value : null),
        hook_key: reflection?.hook_key ?? null,
        skip_when_returned: reflection?.skip_when_returned ?? false,
        watch_fields: reflection?.watch_fields ?? [],
      },
      { emitEvent: false }
    );
    const conditions = buildConditionsGroup(reflection?.conditions);
    this.form.setControl('conditions', conditions);
    this.conditionsGroup.set(conditions);
    this.rebuildConfig(reflection?.hook_key ?? null);

    // The config and the rules store user ids only, and the user lookups read their selection once when they render.
    const userIds = reflection
      ? [
          ...new Set([
            ...extractConfigUserIds(this.schema(), reflection.config),
            ...extractConditionUserIds(reflection.conditions, this.fields()),
          ]),
        ]
      : [];
    this.service
      .userLabels(userIds)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(labels => {
        this.userLabels.set(labels);
        this.ready.set(true);
      });
  }

  /** A new action starts with its own empty config (or the stored one, back on the saved action). */
  private rebuildConfig(key: string | null): void {
    this.hookKey.set(key);
    const reflection = this.reflection();
    const group = buildConfigGroup(
      this.schema(),
      reflection && reflection.hook_key === key ? reflection.config : {}
    );
    this.form.setControl('config', group);
    this.configGroup.set(group);
  }

  save(): void {
    if (this.isSaving()) return;
    this.serverErrors.set({});
    this.saveMessage.set(null);
    // A dual-language value filled in one language only takes it for the other, as the app's other forms do.
    this.formService.syncDualLangControls(this.form);
    this.refreshValidity(this.form);
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const existing = this.reflection();
    const body = this.buildPayload(existing === null);
    this.isSaving.set(true);
    const savedId$ = existing
      ? this.service
          .update(this.flowTemplateId(), existing.id, body)
          .pipe(map(() => existing.id))
      : this.service
          .create(this.flowTemplateId(), body as ReflectionPayload)
          .pipe(map(response => response.data.id));
    savedId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: id => {
        this.isSaving.set(false);
        this.saved.emit(id);
      },
      error: (error: CustomWorkflowApiError) => {
        this.isSaving.set(false);
        this.saveMessage.set(error?.message ?? null);
        this.showServerErrors(error?.errors ?? {});
      },
    });
  }

  cancel(): void {
    this.cancelled.emit();
  }

  /** Create sends everything; update leaves out the immutable scope and step and an empty (= keep) secrets. */
  private buildPayload(creating: boolean): Partial<ReflectionPayload> {
    const raw = this.form.getRawValue();
    const schema = this.schema();
    const { config, secrets } = toConfigPayload(schema, this.configGroup());
    const formScope = this.isFormScope();
    const hasSecrets = Object.keys(secrets).length > 0;

    return {
      ...(creating && {
        scope: this.scope(),
        step_template_id: this.stepTemplateId(),
      }),
      event: raw.event!,
      hook_key: raw.hook_key!,
      config,
      ...((creating || hasSecrets) && { secrets }),
      conditions: this.conditionSet(),
      skip_when_returned: formScope ? false : raw.skip_when_returned,
      // Empty means "no override": the backend then watches the fields the config reads.
      watch_fields:
        formScope && raw.watch_fields.length ? raw.watch_fields : null,
      is_active: this.reflection()?.is_active ?? true,
    };
  }

  private conditionSet(): ConditionSet {
    const raw = this.conditionsGroup().getRawValue() as ConditionSet;
    return {
      match: raw.match,
      rules: raw.rules.map(({ value, ...rule }) =>
        value === null || value === undefined ? rule : { ...rule, value }
      ),
    };
  }

  /**
   * Keeps the 422 `errors` object and puts each message on its control: `config.title` → the `title` control of
   * the config group, `secrets.token` → the `token` control too; the deepest control found takes the message.
   */
  showServerErrors(errors: Record<string, string[]>): void {
    this.serverErrors.set(errors);
    for (const [key, messages] of Object.entries(errors)) {
      let control: AbstractControl = this.form;
      for (const part of errorPath(key)) {
        const child = control.get([part]);
        if (!child) break;
        control = child;
      }
      if (control === this.form) continue;
      const message = messages[0];
      control.setErrors({
        apiError: true,
        apiErrorMessage: message,
        server: message,
      });
      control.markAsDirty();
      control.markAsTouched();
    }
  }

  /**
   * Re-validates every control, bottom-up. That drops the server errors (so saving again is a retry, not a block
   * until the admin edits) and heals ancestors left with a stale status: Angular's form directives re-validate
   * their own subtree without telling their parents when a control is added or replaced.
   */
  private refreshValidity(control: AbstractControl): void {
    if (control instanceof FormGroup) {
      Object.values(control.controls).forEach(child =>
        this.refreshValidity(child)
      );
    } else if (control instanceof FormArray) {
      control.controls.forEach(child => this.refreshValidity(child));
    }
    control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }
}
